import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Crown, Plus, Edit, Trash2, Search, RefreshCw, Download,
  Users, DollarSign, CheckCircle2, XCircle, TrendingUp, Star,
  Shield, Zap, BarChart3, ChevronDown, ChevronUp, AlertTriangle,
  Save, X, ToggleLeft, ToggleRight, Info, LayoutGrid, Table2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

const ROLE_LABELS = { EXHIBITOR: 'Exhibitor', CONTRACTOR: 'Contractor', SUPPLIER: 'Supplier' };
const ROLE_COLORS = {
  EXHIBITOR: { bg: 'bg-blue-100', text: 'text-blue-800', ring: 'ring-blue-200', accent: '#3b82f6' },
  CONTRACTOR: { bg: 'bg-green-100', text: 'text-green-800', ring: 'ring-green-200', accent: '#10b981' },
  SUPPLIER: { bg: 'bg-orange-100', text: 'text-orange-800', ring: 'ring-orange-200', accent: '#f97316' },
};

const formatCurrency = (amount, currency = 'EUR') =>
  new Intl.NumberFormat('tr-TR', { style: 'currency', currency, minimumFractionDigits: 0 }).format(amount);

const FeatureValue = ({ value }) => {
  if (value === true) return <CheckCircle2 className="h-4 w-4 text-green-500" />;
  if (value === false) return <XCircle className="h-4 w-4 text-gray-300" />;
  if (value === -1) return <span className="text-xs font-semibold text-purple-600">Sınırsız</span>;
  return <span className="text-xs font-semibold text-gray-800">{value}</span>;
};

// ─── Plan Editor Modal ────────────────────────────────────────────────────────
const PlanEditorModal = ({ plan, definitions, onSave, onClose, isNew }) => {
  const [form, setForm] = useState(
    plan || {
      role: 'EXHIBITOR', code: '', name: '', description: '',
      price_month: 0, currency: 'EUR', highlight_color: '#6b7280',
      is_active: true, is_free_plan: false, sort_order: 99, features: {}
    }
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const roleDefs = definitions[form.role] || [];

  const setField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const setFeature = (key, value) => setForm(prev => ({ ...prev, features: { ...prev.features, [key]: value } }));

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Plan adı zorunludur.'); return; }
    if (!form.code.trim()) { setError('Plan kodu zorunludur.'); return; }
    setSaving(true);
    setError('');
    try {
      await onSave(form);
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || 'Kaydetme başarısız.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{isNew ? 'Yeni Plan Oluştur' : 'Plan Düzenle'}</h2>
            <p className="text-sm text-gray-500 mt-1">{isNew ? 'Yeni bir abonelik planı tanımla' : `${form.name} planını düzenle`}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {error}
            </div>
          )}

          {/* Basic Info */}
          <div>
            <h3 className="font-semibold text-gray-800 mb-3">Temel Bilgiler</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Rol</label>
                <select
                  value={form.role}
                  onChange={e => setField('role', e.target.value)}
                  disabled={!isNew}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none disabled:bg-gray-50"
                >
                  {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Plan Kodu</label>
                <Input
                  value={form.code}
                  onChange={e => setField('code', e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                  placeholder="free, growth, premium..."
                  disabled={!isNew}
                  className="text-sm"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Plan Adı</label>
                <Input value={form.name} onChange={e => setField('name', e.target.value)} placeholder="Growth" className="text-sm" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Açıklama</label>
                <Input value={form.description} onChange={e => setField('description', e.target.value)} placeholder="Plan açıklaması..." className="text-sm" />
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div>
            <h3 className="font-semibold text-gray-800 mb-3">Fiyatlandırma</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Aylık Fiyat</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">€</span>
                  <Input
                    type="number" min={0}
                    value={form.price_month}
                    onChange={e => setField('price_month', parseFloat(e.target.value) || 0)}
                    className="pl-7 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Para Birimi</label>
                <select
                  value={form.currency}
                  onChange={e => setField('currency', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none"
                >
                  <option value="EUR">EUR</option>
                  <option value="USD">USD</option>
                  <option value="TRY">TRY</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Sıra</label>
                <Input type="number" min={0} value={form.sort_order} onChange={e => setField('sort_order', parseInt(e.target.value) || 0)} className="text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Renk</label>
                <div className="flex gap-2">
                  <input type="color" value={form.highlight_color} onChange={e => setField('highlight_color', e.target.value)} className="h-9 w-12 rounded border border-gray-300 cursor-pointer" />
                  <Input value={form.highlight_color} onChange={e => setField('highlight_color', e.target.value)} className="text-sm" />
                </div>
              </div>
              <div className="flex items-end gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.is_active} onChange={e => setField('is_active', e.target.checked)} className="w-4 h-4 accent-purple-600" />
                  <span className="text-sm text-gray-700">Aktif</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.is_free_plan} onChange={e => setField('is_free_plan', e.target.checked)} className="w-4 h-4 accent-purple-600" />
                  <span className="text-sm text-gray-700">Ücretsiz</span>
                </label>
              </div>
            </div>
          </div>

          {/* Feature Limits */}
          <div>
            <h3 className="font-semibold text-gray-800 mb-1">Özellik Limitleri</h3>
            <p className="text-xs text-gray-500 mb-3">Sayısal alanlar için <strong>-1</strong> girin = sınırsız. Boolean özellikler için checkbox kullanın.</p>
            <div className="space-y-3 bg-gray-50 rounded-xl p-4">
              {roleDefs.map(def => {
                const current = form.features[def.key];
                const isUndefined = current === undefined || current === null;
                return (
                  <div key={def.key} className="flex items-center justify-between gap-4 py-2 border-b border-gray-100 last:border-0">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-800">{def.label}</div>
                      <div className="text-xs text-gray-500">{def.description}</div>
                    </div>
                    <div className="shrink-0">
                      {def.type === 'boolean' ? (
                        <button
                          type="button"
                          onClick={() => setFeature(def.key, !(isUndefined ? false : current))}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                            current ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'
                          }`}
                        >
                          {current ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
                          {current ? 'Açık' : 'Kapalı'}
                        </button>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min={-1}
                            value={isUndefined ? 0 : current}
                            onChange={e => setFeature(def.key, parseInt(e.target.value) ?? 0)}
                            className="w-24 text-sm h-8 text-center"
                          />
                          <span className="text-xs text-gray-400 w-14">{(isUndefined ? 0 : current) === -1 ? '(sınırsız)' : ''}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t">
          <Button variant="outline" onClick={onClose} className="flex-1">İptal</Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 bg-purple-600 hover:bg-purple-700 gap-2"
            data-testid="plan-editor-save-btn"
          >
            <Save className="h-4 w-4" />
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─── Delete Confirm Modal ─────────────────────────────────────────────────────
const DeleteModal = ({ plan, onConfirm, onClose }) => (
  <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-3 rounded-full bg-red-100">
          <Trash2 className="h-5 w-5 text-red-600" />
        </div>
        <div>
          <h3 className="font-bold text-gray-900">Planı Sil</h3>
          <p className="text-sm text-gray-500">Bu işlem geri alınamaz.</p>
        </div>
      </div>
      <p className="text-sm text-gray-700 mb-6">
        <strong>{plan.name}</strong> planını silmek istediğinizden emin misiniz?
        {plan.subscriber_count > 0 && (
          <span className="block mt-2 text-amber-600 font-medium">
            ⚠ Bu planın {plan.subscriber_count} aktif abonesi var. Silinemez.
          </span>
        )}
      </p>
      <div className="flex gap-3">
        <Button variant="outline" onClick={onClose} className="flex-1">İptal</Button>
        <Button
          onClick={onConfirm}
          disabled={plan.subscriber_count > 0}
          className="flex-1 bg-red-600 hover:bg-red-700"
          data-testid="plan-delete-confirm-btn"
        >
          Sil
        </Button>
      </div>
    </div>
  </div>
);

// ─── Plan Card ────────────────────────────────────────────────────────────────
const PlanCard = ({ plan, definitions, onEdit, onDelete, onSimulate }) => {
  const [expanded, setExpanded] = useState(false);
  const roleColor = ROLE_COLORS[plan.role] || ROLE_COLORS.EXHIBITOR;
  const roleDefs = definitions[plan.role] || [];

  return (
    <Card className={`relative border-0 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden`} data-testid={`plan-card-${plan.id}`}>
      {/* Top color stripe */}
      <div className="h-1.5" style={{ backgroundColor: plan.highlight_color || '#6b7280' }} />

      <CardHeader className="pb-3 pt-5">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <CardTitle className="text-base font-bold text-gray-900">{plan.name}</CardTitle>
              <Badge className={`${roleColor.bg} ${roleColor.text} border-0 text-xs`}>
                {ROLE_LABELS[plan.role] || plan.role}
              </Badge>
              {!plan.is_active && <Badge className="bg-gray-100 text-gray-500 border-0 text-xs">Pasif</Badge>}
              {plan.is_free_plan && <Badge className="bg-green-50 text-green-700 border-0 text-xs">Ücretsiz</Badge>}
            </div>
            <p className="text-xs text-gray-500 truncate">{plan.description}</p>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4 pt-0">
        {/* Price */}
        <div className="flex items-baseline gap-1">
          <span className="text-2xl font-bold text-gray-900">
            {plan.price_month === 0 ? '€0' : formatCurrency(plan.price_month, plan.currency)}
          </span>
          <span className="text-sm text-gray-400">/ay</span>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3 text-center border-t border-b py-3">
          <div>
            <div className="text-xl font-bold text-blue-600">{plan.subscriber_count ?? 0}</div>
            <div className="text-xs text-gray-500">Abone</div>
          </div>
          <div>
            <div className="text-xl font-bold text-green-600">
              {formatCurrency((plan.price_month || 0) * (plan.subscriber_count || 0), plan.currency)}
            </div>
            <div className="text-xs text-gray-500">Aylık Gelir</div>
          </div>
        </div>

        {/* Feature preview */}
        <div className="space-y-1.5">
          {roleDefs.slice(0, expanded ? 20 : 4).map(def => {
            const val = plan.features?.[def.key];
            return (
              <div key={def.key} className="flex items-center justify-between text-sm">
                <span className="text-gray-600 truncate pr-2">{def.label}</span>
                <FeatureValue value={val === undefined ? (def.type === 'boolean' ? false : 0) : val} />
              </div>
            );
          })}
        </div>

        {roleDefs.length > 4 && (
          <button
            onClick={() => setExpanded(v => !v)}
            className="text-xs text-purple-600 hover:text-purple-700 flex items-center gap-1"
          >
            {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            {expanded ? 'Daha az göster' : `+${roleDefs.length - 4} özellik daha`}
          </button>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            className="flex-1 gap-1.5 text-xs"
            onClick={onEdit}
            data-testid={`plan-edit-btn-${plan.id}`}
          >
            <Edit className="h-3.5 w-3.5" />
            Düzenle
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50"
            onClick={onSimulate}
            data-testid={`plan-simulate-btn-${plan.id}`}
          >
            <BarChart3 className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
            onClick={onDelete}
            data-testid={`plan-delete-btn-${plan.id}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Impact Analysis Modal (Plan Simülatörü) ─────────────────────────────────
const ImpactModal = ({ plan, onClose }) => {
  const [impact, setImpact] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchImpact = async () => {
      try {
        const res = await axios.get(`${API}/api/ultra/plans-managed/${plan.id}/impact`, { headers: authHeaders() });
        setImpact(res.data);
      } catch { setImpact({ subscriber_count: 0, orgs: [], features: plan.features }); }
      finally { setLoading(false); }
    };
    fetchImpact();
  }, [plan.id]);

  const featureList = Object.entries(plan.features || {});

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Plan Simülatörü</h2>
            <p className="text-sm text-gray-500">{plan.name} ({ROLE_LABELS[plan.role]})</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X className="h-5 w-5 text-gray-500" /></button>
        </div>

        <div className="p-6 space-y-5">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin h-8 w-8 border-2 border-purple-500 border-t-transparent rounded-full" />
            </div>
          ) : (
            <>
              {/* KPI */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Aktif Abone', value: impact?.subscriber_count || 0, color: 'text-blue-600' },
                  { label: 'Aylık Gelir', value: formatCurrency((plan.price_month || 0) * (impact?.subscriber_count || 0), plan.currency), color: 'text-green-600' },
                  { label: 'Özellik Sayısı', value: featureList.length, color: 'text-purple-600' },
                ].map(k => (
                  <div key={k.label} className="text-center p-3 bg-gray-50 rounded-xl">
                    <div className={`text-xl font-bold ${k.color}`}>{k.value}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{k.label}</div>
                  </div>
                ))}
              </div>

              {/* Feature limits */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Mevcut Limitler</h4>
                <div className="space-y-2 bg-gray-50 rounded-xl p-4">
                  {featureList.map(([key, val]) => (
                    <div key={key} className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">{FEATURE_LABELS[key] || key.replace(/_/g, ' ')}</span>
                      <FeatureValue value={val} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Affected orgs */}
              {impact?.orgs?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">
                    Bu Plandaki Organizasyonlar ({impact.orgs.length})
                  </h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {impact.orgs.map(org => (
                      <div key={org.org_id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 text-sm">
                        <span className="font-medium text-gray-800 truncate">{org.name || org.org_id}</span>
                        <div className="flex items-center gap-3 shrink-0 ml-2">
                          {org.country && <span className="text-gray-400 text-xs">{org.country}</span>}
                          {org.usage_label && (
                            <span className="text-xs text-blue-600 font-medium">{org.usage_label}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {impact?.subscriber_count === 0 && (
                <div className="text-center py-4 text-gray-400 text-sm">
                  Bu planda henüz aktif abone yok.
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-700">
                <strong>Simülasyon:</strong> Bu plandaki {impact?.subscriber_count || 0} abone, limitleri değiştirdiğinizde anında etkilenecek.
                Değişiklikler gerçek zamanlı uygulanır.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// FEATURE_LABELS for impact modal
const FEATURE_LABELS = {
  active_briefs: 'Aktif Brief',
  proposal_views: 'Teklif Görüntüleme',
  analytics: 'Analitik',
  csv_export: 'CSV Export',
  platform_benchmark: 'Platform Benchmark',
  brief_templates: 'Brief Şablonları',
  direct_invites: 'Direkt Davet Hakkı',
  budget_intelligence: 'Bütçe Zekası',
  brief_boost_credits: 'Brief Boost Kredisi',
  custom_branding: 'Özel Markalama',
  api_access: 'API Erişimi',
  proposals_per_month: 'Aylık Teklif',
  spotlight_badge: 'Öne Çıkan Rozet',
  team_members: 'Ekip Üyeleri',
  portfolio_items: 'Portföy Görseli',
  quick_quote_templates: 'Hızlı Teklif Şablonu',
  priority_listing: 'Öncelikli Listeleme',
  coverage_cities: 'Kapsama Şehri (Maks.)',
  catalogue_items: 'Katalog Ürünü',
  lead_notifications: 'Lead Bildirimi',
  featured_listing: 'Öne Çıkan Liste',
};

// Render a single feature value
const FeatureVal = ({ val }) => {
  if (val === true)  return <span className="text-green-600 font-bold text-base">✓</span>;
  if (val === false) return <span className="text-gray-300 font-bold text-base">✗</span>;
  if (val === -1)    return <span className="text-gray-700 font-semibold text-sm">Sınırsız</span>;
  if (val === 0)     return <span className="text-gray-400 text-sm">0</span>;
  return <span className="text-gray-800 font-semibold text-sm">{val}</span>;
};

// Comparison Table for ONE role
const ComparisonTable = ({ role, plans }) => {
  const rolePlans = plans.filter(p => p.role === role).sort((a,b) => (a.sort_order||0)-(b.sort_order||0));
  if (rolePlans.length === 0) return null;

  const featureKeys = Object.keys(rolePlans[0]?.features || {});
  const ROLE_LABEL = { EXHIBITOR: 'Sergileyici (Exhibitor)', CONTRACTOR: 'Stand Tasarımcısı (Contractor)', SUPPLIER: 'Tedarikçi (Supplier)' };
  const ROLE_COLOR = { EXHIBITOR: 'bg-orange-500', CONTRACTOR: 'bg-teal-600', SUPPLIER: 'bg-purple-600' };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Role header */}
      <div className={`px-6 py-3 ${ROLE_COLOR[role]} text-white`}>
        <span className="font-bold text-sm tracking-wide">{ROLE_LABEL[role]}</span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50/60">
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide w-48">Özellik</th>
              {rolePlans.map(p => (
                <th key={p.id} className="px-5 py-3 text-center min-w-[110px]">
                  <div className="font-bold text-gray-900 text-sm">{p.name}</div>
                  <div className="text-xs text-gray-400 mt-0.5 font-normal">
                    {p.price_month === 0 ? 'Ücretsiz' : `€${p.price_month}/ay`}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {featureKeys.map(key => (
              <tr key={key} className="hover:bg-gray-50/50 transition-colors">
                <td className="px-6 py-3 font-semibold text-gray-700 text-xs">
                  {FEATURE_LABELS[key] || key}
                </td>
                {rolePlans.map(p => (
                  <td key={p.id} className="px-5 py-3 text-center">
                    <FeatureVal val={p.features?.[key]} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};


const UltraPlans = () => {
  const [plans, setPlans] = useState([]);
  const [definitions, setDefinitions] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeRole, setActiveRole] = useState('all');
  const [viewMode, setViewMode] = useState('cards'); // 'cards' | 'compare'
  const [search, setSearch] = useState('');
  const [editingPlan, setEditingPlan] = useState(null);
  const [isNewPlan, setIsNewPlan] = useState(false);
  const [deletingPlan, setDeletingPlan] = useState(null);
  const [simulatingPlan, setSimulatingPlan] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const h = authHeaders();
      const [plansRes, defsRes] = await Promise.allSettled([
        axios.get(`${API}/api/ultra/plans-managed`, { headers: h }),
        axios.get(`${API}/api/ultra/plan-definitions`, { headers: h }),
      ]);
      if (plansRes.status === 'fulfilled') setPlans(plansRes.value.data?.items || []);
      if (defsRes.status === 'fulfilled') setDefinitions(defsRes.value.data?.definitions || {});
    } catch (e) {
      console.error('Fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async (form) => {
    const h = authHeaders();
    if (isNewPlan) {
      await axios.post(`${API}/api/ultra/plans-managed`, form, { headers: h });
      showToast('Plan başarıyla oluşturuldu.');
    } else {
      await axios.patch(`${API}/api/ultra/plans-managed/${form.id}`, form, { headers: h });
      showToast('Plan başarıyla güncellendi.');
    }
    await fetchData();
  };

  const handleDelete = async () => {
    if (!deletingPlan) return;
    try {
      await axios.delete(`${API}/api/ultra/plans-managed/${deletingPlan.id}`, { headers: authHeaders() });
      showToast('Plan silindi.', 'success');
      setDeletingPlan(null);
      await fetchData();
    } catch (e) {
      showToast(e.response?.data?.detail || 'Silme başarısız.', 'error');
      setDeletingPlan(null);
    }
  };

  const handleSeedDefaults = async () => {
    try {
      await axios.post(`${API}/api/ultra/plans-managed/seed`, {}, { headers: authHeaders() });
      showToast('Varsayılan planlar yüklendi.');
      await fetchData();
    } catch (e) {
      showToast('Seed başarısız.', 'error');
    }
  };

  const filteredPlans = plans.filter(p => {
    const matchRole = activeRole === 'all' || p.role === activeRole;
    const matchSearch = !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.code.toLowerCase().includes(search.toLowerCase());
    return matchRole && matchSearch;
  });

  // KPI stats
  const totalRevenue = plans.reduce((s, p) => s + (p.price_month || 0) * (p.subscriber_count || 0), 0);
  const totalSubscribers = plans.reduce((s, p) => s + (p.subscriber_count || 0), 0);
  const activePlans = plans.filter(p => p.is_active).length;

  const TABS = [
    { key: 'all', label: 'Tümü', count: plans.length },
    { key: 'EXHIBITOR', label: 'Exhibitor', count: plans.filter(p => p.role === 'EXHIBITOR').length },
    { key: 'CONTRACTOR', label: 'Contractor', count: plans.filter(p => p.role === 'CONTRACTOR').length },
    { key: 'SUPPLIER', label: 'Supplier', count: plans.filter(p => p.role === 'SUPPLIER').length },
  ];

  return (
    <div className="space-y-6 bg-gray-50 -m-3 sm:-m-6 p-3 sm:p-6 min-h-screen">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-6 right-6 z-50 px-5 py-3 rounded-xl shadow-xl text-sm font-medium flex items-center gap-2 transition-all ${
          toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-gray-900 text-white'
        }`} data-testid="plan-toast">
          {toast.type === 'error' ? <XCircle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Canlı Plan Yöneticisi</h1>
            <p className="text-sm text-gray-500 mt-1">Fiyatları ve limitleri değiştirin — platform anında güncellenir</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {/* View Mode Toggle */}
            <div className="flex items-center gap-0 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('cards')}
                data-testid="view-cards-btn"
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${viewMode === 'cards' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <LayoutGrid className="w-3.5 h-3.5" /> Kartlar
              </button>
              <button
                onClick={() => setViewMode('compare')}
                data-testid="view-compare-btn"
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${viewMode === 'compare' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <Table2 className="w-3.5 h-3.5" /> Karşılaştır
              </button>
            </div>
            <Button variant="outline" size="sm" onClick={fetchData} className="gap-1.5">
              <RefreshCw className="h-4 w-4" /> Yenile
            </Button>
            <Button variant="outline" size="sm" onClick={handleSeedDefaults} className="gap-1.5 text-orange-600 hover:text-orange-700">
              <Download className="h-4 w-4" /> Varsayılanları Yükle
            </Button>
            <Button
              size="sm"
              onClick={() => { setEditingPlan(null); setIsNewPlan(true); }}
              className="gap-1.5 bg-purple-600 hover:bg-purple-700"
              data-testid="create-plan-btn"
            >
              <Plus className="h-4 w-4" /> Yeni Plan
            </Button>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { label: 'Aylık Gelir', value: formatCurrency(totalRevenue, 'EUR'), sub: 'tüm planlardan', icon: DollarSign, color: 'from-green-500 to-green-600' },
          { label: 'Toplam Abone', value: totalSubscribers.toLocaleString(), sub: 'aktif aboneler', icon: Users, color: 'from-blue-500 to-blue-600' },
          { label: 'Aktif Plan', value: activePlans, sub: `${plans.length} plandan`, icon: Crown, color: 'from-purple-500 to-purple-600' },
        ].map(kpi => (
          <Card key={kpi.label} className="border-0 shadow-sm">
            <CardContent className="p-5 flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{kpi.label}</p>
                <p className="text-2xl font-bold text-gray-900 mt-0.5">{kpi.value}</p>
                <p className="text-xs text-gray-400 mt-0.5">{kpi.sub}</p>
              </div>
              <div className={`p-3 rounded-full bg-gradient-to-br ${kpi.color}`}>
                <kpi.icon className="h-5 w-5 text-white" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tabs + Search */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          {/* Role tabs */}
          <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
            {TABS.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveRole(tab.key)}
                data-testid={`role-tab-${tab.key}`}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  activeRole === tab.key
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                  activeRole === tab.key ? 'bg-purple-100 text-purple-700' : 'bg-gray-200 text-gray-500'
                }`}>{tab.count}</span>
              </button>
            ))}
          </div>
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Plan ara..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 h-9 w-48 text-sm"
              data-testid="plan-search-input"
            />
          </div>
        </div>
      </div>

      {/* Plans Grid / Comparison View */}
      {viewMode === 'compare' ? (
        <div className="space-y-6">
          <ComparisonTable role="EXHIBITOR" plans={plans} />
          <ComparisonTable role="CONTRACTOR" plans={plans} />
          <ComparisonTable role="SUPPLIER" plans={plans} />
        </div>
      ) : loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-5">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i} className="animate-pulse border-0 shadow-sm">
              <div className="h-1.5 bg-gray-200 rounded-t-xl" />
              <CardContent className="p-5 space-y-3">
                <div className="h-5 bg-gray-200 rounded w-3/4" />
                <div className="h-3 bg-gray-200 rounded w-full" />
                <div className="h-8 bg-gray-200 rounded w-1/2" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredPlans.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
          <Crown className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">Plan bulunamadı</p>
          <p className="text-gray-400 text-sm mt-1">Filtrelerinizi değiştirin ya da yeni plan oluşturun.</p>
          <Button
            size="sm"
            onClick={handleSeedDefaults}
            className="mt-4 bg-purple-600 hover:bg-purple-700 gap-2"
          >
            <Download className="h-4 w-4" /> Varsayılan Planları Yükle
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-5">
          {filteredPlans.map(plan => (
            <PlanCard
              key={plan.id}
              plan={plan}
              definitions={definitions}
              onEdit={() => { setEditingPlan(plan); setIsNewPlan(false); }}
              onDelete={() => setDeletingPlan(plan)}
              onSimulate={() => setSimulatingPlan(plan)}
            />
          ))}
        </div>
      )}}

      {/* Editor Modal */}
      {(editingPlan !== null || isNewPlan) && (
        <PlanEditorModal
          plan={isNewPlan ? null : editingPlan}
          definitions={definitions}
          isNew={isNewPlan}
          onSave={handleSave}
          onClose={() => { setEditingPlan(null); setIsNewPlan(false); }}
        />
      )}

      {/* Delete Modal */}
      {deletingPlan && (
        <DeleteModal
          plan={deletingPlan}
          onConfirm={handleDelete}
          onClose={() => setDeletingPlan(null)}
        />
      )}

      {/* Simulate Modal */}
      {simulatingPlan && (
        <ImpactModal
          plan={simulatingPlan}
          onClose={() => setSimulatingPlan(null)}
        />
      )}
    </div>
  );
};

export default UltraPlans;
